import { Link } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { UserProfileManager, UserProfile } from '../utils/userProfile'
import { ProfileSetup } from '../components/ProfileSetup'
import '../styles/Home.css'

const gameCategories = {
  card: {
    title: '🃏 Card Games',
    description: 'Classic card game favorites',
    games: [
      {
        id: 'bigtwo',
        name: 'Big Two',
        emoji: '🃏',
        description: 'Popular Chinese card game — play higher combos to shed your hand first',
        players: '2–4 Players',
        path: '/bigtwo',
        gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
        category: 'card' as const,
      },
      {
        id: 'gofish',
        name: 'Go Fish',
        emoji: '🐟',
        description: 'Ask, fish, and collect matching pairs — easy to learn, hard to put down',
        players: '2–4 Players',
        path: '/gofish',
        gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        category: 'card' as const,
      },
    ],
  },
  board: {
    title: '🎯 Board & Strategy',
    description: 'Think, deduce, and outwit your opponent',
    games: [
      {
        id: 'guesswho',
        name: 'Guess Who?',
        emoji: '🕵️',
        description: 'Eliminate suspects one clue at a time — figure out their mystery character first',
        players: '2 Players',
        path: '/guesswho',
        gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        category: 'board' as const,
      },
      {
        id: 'tictactoe',
        name: 'Ultimate Tic-Tac-Toe',
        emoji: '⭕',
        description: '9 boards in one — every move decides which board your opponent must play next',
        players: '2 Players',
        path: '/tictactoe',
        gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        category: 'board' as const,
      },
      {
        id: 'pictionary',
        name: 'Pictionary',
        emoji: '🎨',
        description: 'Sketch fast, guess faster — your team earns points before time runs out',
        players: '2+ Players',
        path: '/pictionary',
        gradient: 'linear-gradient(135deg, #f46b45 0%, #d63031 100%)',
        category: 'board' as const,
      },
    ],
  },
}

// Helper functions to get game display info
const getGameName = (gameId: string): string => {
  const gameMap: Record<string, string> = {
    guesswho: 'Guess Who?',
    tictactoe: 'Ultimate Tic-Tac-Toe',
    gofish: 'Go Fish',
    bigtwo: 'Big Two',
    werewolf: 'Werewolf',
    pictionary: 'Pictionary',
    themind: 'The Mind',
    codenames: 'Codenames',
    wavelength: 'Wavelength',
  }
  return gameMap[gameId] || gameId
}

const getGameEmoji = (gameId: string): string => {
  const emojiMap: Record<string, string> = {
    guesswho: '🕵️',
    tictactoe: '⭕',
    gofish: '🐠',
    bigtwo: '🃏',
    werewolf: '🐺',
    pictionary: '🎨',
    themind: '🧠',
    codenames: '🕵️‍♂️',
    wavelength: '📻',
  }
  return emojiMap[gameId] || '🎮'
}

export default function Home() {
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [showProfileSetup, setShowProfileSetup] = useState(false)
  const [showDetailedStats, setShowDetailedStats] = useState(false)

  const favoriteGame = profile ? UserProfileManager.getFavoriteGame() : null

  useEffect(() => {
    const existingProfile = UserProfileManager.get()
    if (existingProfile) {
      setProfile(existingProfile)
    } else {
      setShowProfileSetup(true)
    }
  }, [])

  const handleProfileComplete = () => {
    setShowProfileSetup(false)
    setProfile(UserProfileManager.get())
  }

  const handleEditProfile = () => {
    setShowProfileSetup(true)
  }

  const handleDeleteProfile = () => {
    if (confirm('Are you sure you want to delete your profile? This cannot be undone.')) {
      UserProfileManager.delete()
      setProfile(null)
      setShowProfileSetup(true)
    }
  }

  return (
    <div className="home-container">
      {/* Profile Setup Modal */}
      {showProfileSetup && <ProfileSetup onComplete={handleProfileComplete} />}

      {/* Hero Section */}
      <div className="hero-section">
        <div className="logo-container">
          <img src="/logo.svg" alt="zGames Logo" className="hero-logo" />
        </div>

        <h1 className="hero-title">
          Welcome to <span className="gradient-text">zGames</span>
        </h1>
        <p className="hero-subtitle">
          Play multiplayer games online with friends - no downloads, just fun!
        </p>
        <div className="hero-features">
          <div className="feature-badge">
            <span className="feature-icon">🎮</span>
            <span>5 Games</span>
          </div>
          <div className="feature-badge">
            <span className="feature-icon">🌐</span>
            <span>Online Multiplayer</span>
          </div>
          <div className="feature-badge">
            <span className="feature-icon">🎙️</span>
            <span>Voice Chat</span>
          </div>
          <div className="feature-badge">
            <span className="feature-icon">📱</span>
            <span>Mobile Ready</span>
          </div>
        </div>

        {/* User Profile Display */}
        {profile && (
          <>
            <div className="user-profile-card">
              <div className="profile-avatar">{profile.avatar}</div>
              <div className="profile-info">
                <h3 className="profile-name">{profile.name}</h3>
                <div className="profile-stats">
                  <span className="stat-item">
                    <span className="stat-icon">🎮</span>
                    {profile.gamesPlayed} games
                  </span>
                  <span className="stat-item">
                    <span className="stat-icon">🏆</span>
                    {profile.wins} wins
                  </span>
                  {profile.gamesPlayed > 0 && (
                    <span className="stat-item">
                      <span className="stat-icon">📊</span>
                      {Math.round((profile.wins / profile.gamesPlayed) * 100)}% win rate
                    </span>
                  )}
                </div>
                {favoriteGame && (
                  <div className="favorite-game">
                    ⭐ Favorite: {getGameName(favoriteGame.game)}
                  </div>
                )}
              </div>
              <div className="profile-actions">
                <button
                  onClick={() => setShowDetailedStats(!showDetailedStats)}
                  className="profile-btn stats-btn"
                  title="View detailed stats"
                >
                  {showDetailedStats ? '📊' : '📈'}
                </button>
                <button
                  onClick={handleEditProfile}
                  className="profile-btn edit-btn"
                  title="Edit profile"
                >
                  ✏️
                </button>
                <button
                  onClick={handleDeleteProfile}
                  className="profile-btn delete-btn"
                  title="Delete profile"
                >
                  🗑️
                </button>
              </div>
            </div>

            {/* Detailed Stats Panel */}
            {showDetailedStats && profile.gameStats && (
              <div className="detailed-stats-panel">
                <h3>📊 Your Game Statistics</h3>
                <div className="game-stats-grid">
                  {Object.entries(profile.gameStats)
                    .filter(([_, stats]) => stats.played > 0)
                    .sort((a, b) => b[1].played - a[1].played)
                    .map(([gameType, stats]) => (
                      <div key={gameType} className="game-stat-card">
                        <div className="game-stat-header">
                          <span className="game-stat-emoji">{getGameEmoji(gameType)}</span>
                          <span className="game-stat-name">{getGameName(gameType)}</span>
                        </div>
                        <div className="game-stat-details">
                          <div className="stat-row">
                            <span>Played:</span>
                            <strong>{stats.played}</strong>
                          </div>
                          <div className="stat-row">
                            <span>Wins:</span>
                            <strong className="wins">{stats.wins}</strong>
                          </div>
                          <div className="stat-row">
                            <span>Win Rate:</span>
                            <strong>{Math.round((stats.wins / stats.played) * 100)}%</strong>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Games Section - Organized by Category */}
      <div className="games-section">
        {Object.entries(gameCategories).map(([categoryKey, category]) => (
          <div key={categoryKey} className="game-category">
            <div className="category-header">
              <h2 className="category-title">{category.title}</h2>
              <p className="category-description">{category.description}</p>
            </div>
            <div className="games-grid">
              {category.games.map((game) => (
                <Link
                  key={game.id}
                  to={game.path}
                  className="game-card-modern"
                  style={{ '--card-gradient': game.gradient } as React.CSSProperties}
                >
                  <div className="game-card-background"></div>
                  <div className="game-card-content">
                    <div className="game-icon">{game.emoji}</div>
                    <h3 className="game-title">{game.name}</h3>
                    <p className="game-description">{game.description}</p>
                    <div className="game-footer">
                      <span className="players-badge-modern">
                        <span className="players-icon">👥</span>
                        {game.players}
                      </span>
                      <span className="play-arrow">Play →</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <footer className="home-footer">
        <p>Built with ❤️ for gamers everywhere</p>
        <p className="footer-subtext">
          Open source • WebRTC powered • Free forever
        </p>
      </footer>
    </div>
  )
}
